/**
 * Hooks TanStack Query — TEXTPRINT Sprint 3 back-office admin CommandesBoutique.
 * Brief SIM-PLATFORM-BOUTIQUE-2026-001 §B2 + brief extension §3.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  commandesBoutiqueApi,
  lignesCommandeBoutiqueApi,
  paiementsBoutiqueApi,
  livraisonBoutiqueApi,
  changerStatut,
  type CommandeBoutiqueListParams,
  type CreateCommandeBoutiquePayload,
  type UpdateCommandeBoutiquePayload,
  type CreateLigneCommandeBoutiquePayload,
  type UpdateLigneCommandeBoutiquePayload,
  type CreatePaiementBoutiquePayload,
  type UpdatePaiementBoutiquePayload,
  type UpsertLivraisonBoutiquePayload,
} from '@/api/textprint-commandes-boutique';
import type { StatutCommandeBoutique } from '@/types';

const K = {
  root:     ['textprint', 'commandes-boutique'] as const,
  list:     ['textprint', 'commandes-boutique', 'list'] as const,
  detail:   (id: string) => ['textprint', 'commandes-boutique', 'detail', id] as const,
};

// ── COMMANDES ────────────────────────────────────────────────────────────

export const useCommandesBoutique = (params: CommandeBoutiqueListParams = {}) =>
  useQuery({
    queryKey:  [...K.list, params],
    queryFn:   () => commandesBoutiqueApi.list(params),
    staleTime: 30_000,
  });

export const useCommandeBoutique = (id: string | undefined) =>
  useQuery({
    queryKey:  id ? K.detail(id) : ['textprint', 'commandes-boutique', 'detail', 'none'],
    queryFn:   () => commandesBoutiqueApi.get(id!),
    enabled:   !!id,
    staleTime: 15_000,
  });

export const useCreateCommandeBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCommandeBoutiquePayload) => commandesBoutiqueApi.create(payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.root }),
  });
};

export const useUpdateCommandeBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCommandeBoutiquePayload }) =>
      commandesBoutiqueApi.update(id, payload),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: K.list });
      qc.invalidateQueries({ queryKey: K.detail(vars.id) });
    },
  });
};

export const useDeleteCommandeBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => commandesBoutiqueApi.remove(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.root }),
  });
};

export const useChangerStatutCommandeBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, statut }: { id: string; statut: StatutCommandeBoutique }) =>
      changerStatut(id, statut),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: K.list });
      qc.invalidateQueries({ queryKey: K.detail(vars.id) });
    },
  });
};

// ── LIGNES ───────────────────────────────────────────────────────────────

export const useCreateLigneCommandeBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ commandeId, payload }: { commandeId: string; payload: CreateLigneCommandeBoutiquePayload }) =>
      lignesCommandeBoutiqueApi.create(commandeId, payload),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: K.list });
      qc.invalidateQueries({ queryKey: K.detail(vars.commandeId) });
    },
  });
};

export const useUpdateLigneCommandeBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      commandeId, lineId, payload,
    }: { commandeId: string; lineId: string; payload: UpdateLigneCommandeBoutiquePayload }) =>
      lignesCommandeBoutiqueApi.update(commandeId, lineId, payload),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: K.list });
      qc.invalidateQueries({ queryKey: K.detail(vars.commandeId) });
    },
  });
};

export const useDeleteLigneCommandeBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ commandeId, lineId }: { commandeId: string; lineId: string }) =>
      lignesCommandeBoutiqueApi.remove(commandeId, lineId),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: K.list });
      qc.invalidateQueries({ queryKey: K.detail(vars.commandeId) });
    },
  });
};

// ── PAIEMENTS ────────────────────────────────────────────────────────────

export const useCreatePaiementBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ commandeId, payload }: { commandeId: string; payload: CreatePaiementBoutiquePayload }) =>
      paiementsBoutiqueApi.create(commandeId, payload),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: K.list });
      qc.invalidateQueries({ queryKey: K.detail(vars.commandeId) });
    },
  });
};

export const useUpdatePaiementBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      commandeId, paiementId, payload,
    }: { commandeId: string; paiementId: string; payload: UpdatePaiementBoutiquePayload }) =>
      paiementsBoutiqueApi.update(commandeId, paiementId, payload),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: K.list });
      qc.invalidateQueries({ queryKey: K.detail(vars.commandeId) });
    },
  });
};

// ── LIVRAISON ────────────────────────────────────────────────────────────

export const useUpsertLivraisonBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ commandeId, payload }: { commandeId: string; payload: UpsertLivraisonBoutiquePayload }) =>
      livraisonBoutiqueApi.createOrUpdate(commandeId, payload),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: K.list });
      qc.invalidateQueries({ queryKey: K.detail(vars.commandeId) });
    },
  });
};
